import { Link } from 'react-router';
import { formatDistanceToNow } from 'date-fns';
import { Notification } from '@/types';
import {
  ArrowLeftRight,
  ClipboardCheck,
  AlertTriangle,
  Bell,
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface DisplayConfig {
  icon: React.ComponentType<{ className?: string }>;
  iconClass: string;
  dotClass: string;
  bgClass: string;
}

const getDisplayConfig = (title?: string): DisplayConfig => {
  const t = title?.toLowerCase() ?? '';

  if (t.includes('assign'))
    return {
      icon: ClipboardCheck,
      iconClass: 'text-blue-500',
      dotClass: 'bg-blue-500',
      bgClass: 'bg-blue-500/10 border-blue-500/20',
    };

  if (t.includes('transfer'))
    return {
      icon: ArrowLeftRight,
      iconClass: 'text-amber-500',
      dotClass: 'bg-amber-500',
      bgClass: 'bg-amber-500/10 border-amber-500/20',
    };

  if (t.includes('claim'))
    return {
      icon: ClipboardCheck,
      iconClass: 'text-green-500',
      dotClass: 'bg-green-500',
      bgClass: 'bg-green-500/10 border-green-500/20',
    };

  if (t.includes('alert') || t.includes('system') || t.includes('warning'))
    return {
      icon: AlertTriangle,
      iconClass: 'text-red-500',
      dotClass: 'bg-red-500',
      bgClass: 'bg-red-500/10 border-red-500/20',
    };

  // Default
  return {
    icon: Bell,
    iconClass: 'text-muted-foreground',
    dotClass: 'bg-muted-foreground',
    bgClass: 'bg-muted border-border',
  };
};

// ─── Component ────────────────────────────────────────────────────────────────

interface NotificationItemProps {
  notification: Notification;
  isLast?: boolean;
}

export const NotificationItem = ({
  notification,
  isLast = false,
}: NotificationItemProps) => {
  const config = getDisplayConfig(notification.title);
  const Icon = config.icon;
  const isUnread = !notification.isRead; // ✅ uses isRead not readAt

  const content = (
    <div
      className={cn(
        'flex items-start gap-3 px-4 py-3.5 transition-colors',
        'hover:bg-muted/50',
        isUnread && 'bg-primary/[0.03]',
        !isLast && 'border-b border-border/60',
      )}
    >
      {/* Icon */}
      <div
        className={cn(
          'mt-0.5 w-8 h-8 rounded-lg border flex items-center justify-center shrink-0',
          config.bgClass,
        )}
      >
        <Icon className={cn('w-3.5 h-3.5', config.iconClass)} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex items-start justify-between gap-2">
          <p
            className={cn(
              'text-sm leading-snug',
              isUnread
                ? 'font-semibold text-foreground'
                : 'font-medium text-foreground/80',
            )}
          >
            {notification.title ?? 'Notification'}
          </p>
          {isUnread && (
            <span
              className={cn(
                'mt-1.5 w-2 h-2 rounded-full shrink-0',
                config.dotClass,
              )}
            />
          )}
        </div>

        {notification.message && (
          <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">
            {notification.message}
          </p>
        )}

        <p className="text-[10px] text-muted-foreground/70 tabular-nums">
          {formatDistanceToNow(
            notification.createdAt
              ? new Date(notification.createdAt)
              : new Date(),
            { addSuffix: true },
          )}
        </p>
      </div>
    </div>
  );

  if (notification.link) {
    return (
      <Link
        to={notification.link}
        className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {content}
      </Link>
    );
  }

  return <div>{content}</div>;
};
